import { readFrameLight, type FrameLight } from "../effects/frame-light.ts";
import type { StripTiming } from "../assets/timing.ts";
import { FOOTPRINT_DEFAULT_CELL, depthAtCell, groundCellOf } from "./footprint-cell.ts";
import { isEmptyFrameDepth, type FrameDepth } from "./ground-depth.ts";
import { validateFrameDepth } from "./ground-depth-validate.ts";
import type { PropFootprint } from "./footprint.ts";
import type { FrameMarks } from "./stand-map.ts";

/**
 * Hand-authored footprints (ADR-040): a frame's collision as cells relative to the frame's bottom-left,
 * `[column, rowsUpFromBottom]` in collision cells, edited in the footprint panel and stored per pack as
 * data. Converting to pixel runs lets the compiler stamp them exactly like derived footprints.
 */

/**
 * One frame's override: `none` for no collision, or an object with the cells to block (absent: the
 * derived footprint stays) and `ground`, which makes the frame draw under characters (true) or sort by
 * row (false) whatever the game's naming policy says (ADR-040), `amongProps`, which makes a `ground` frame lie flat
 * among props (true: it sorts by the top edge of its art) or draw under every prop as it always has (false or
 * absent), read only where the frame is `ground`, kept and ignored elsewhere (ADR-040's amendment of 2026-10-06,
 * decision 6), and `fades`, which says whether the occluder
 * fade and the cone of sight may dim the frame (false: it never fades, as a building; true: it fades) whatever
 * the game's naming policy says (`DaedalusGameAdapter.propNoFade`). Each of those is independent of the others.
 * `passage` holds the cells of the frame's deck, under which a placed prop opens the ground, and `surface` the
 * name of what that deck is made of (ADR-040's amendment of 2026-10-05); an absent or empty `passage` is a
 * frame with no deck. `depth` holds the parts of the frame that sort by lines of their own (ADR-098 decision 8),
 * on the same cells, and is independent of every other field as the flags are: a merge that saves cells, a flag
 * or a passage keeps it, saving it keeps them, `none` with a depth is kept as `{ cells: [], depth }`, a Reset of
 * the footprint keeps it, and a depth that names no floor and holds no rectangle removes it. A stored entry may
 * hold a `depth` that is not a `FrameDepth`, written by hand or by a later build: it is carried as it is,
 * `frameDepths` passes over it and `depthIssues` names it, so it never stops a read. `timing` times the strip
 * the frame starts (ADR-107 decision 21); `light` is the frame's light (ADR-141's amendment of 2026-11-01), replaced whole by a
 * change and removed by `null` as `timing` is; `null` in a change removes the stored timing and is never stored.
 * `band` is a depth for Auto (ADR-113 decision 14, `footprint-band.ts`): a whole number of art pixels from 1 to the
 * frame's height, the rows above the base the derived footprint takes in place of the half-width rule. It is
 * independent of every other field as the flags are, kept through `none` and a Reset, never changes what `cells`
 * block (cells in the same entry win), and `null` in a change removes it and is never stored. `cell` is the side in
 * pixels of `cells` and `passage` together, 8 or 4 (ADR-113 decision 7); absent means the authoring cell, so an entry
 * stored before the field reads exactly as it did. `behind` and `front` (ADR-132 decision 2) are the cells, counted in the same
 * `cell` as `cells`, where a character standing is drawn behind the prop or in front of it; a cell is never both Behind and In front, and may be blocked as well across
 * `cells`, `behind` and `front`, and they may lie up to `STAND_REACH` cells outside the art (`standMarksIssue`).
 */
export type FootprintOverride = "none" | { readonly cells?: readonly (readonly [number, number])[]; readonly ground?: boolean; readonly amongProps?: boolean; readonly fades?: boolean; readonly passage?: readonly (readonly [number, number])[]; readonly surface?: string; readonly depth?: FrameDepth; readonly timing?: StripTiming | null; readonly light?: FrameLight | null; readonly band?: number | null; readonly cell?: number; readonly behind?: readonly (readonly [number, number])[]; readonly front?: readonly (readonly [number, number])[] };

/**
 * A footprint from cells: each cell becomes `cellPixels` full-width runs, anchored at the frame bottom
 * (`base = h`), with adjacent cells on a row merged into one run. Cells outside the frame are dropped.
 *
 * @param cells `[column, rowsUp]` cells.
 * @param w Frame width in pixels.
 * @param h Frame height in pixels.
 * @param cellPixels Pixels per collision cell.
 * @returns The footprint.
 */
export function footprintFromCells(cells: readonly (readonly [number, number])[], w: number, h: number, cellPixels: number): PropFootprint {
  const columns = Math.ceil(w / cellPixels);
  const rows = Math.ceil(h / cellPixels);
  const runs: [number, number, number][] = [];
  const sorted = [...cells].filter(([cx, cy]) => cx >= 0 && cy >= 0 && cx < columns && cy < rows).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  for (const [cx, cy] of sorted) {
    const start = cx * cellPixels;
    const end = Math.min(w - 1, start + cellPixels - 1);
    for (let dy = 0; dy < cellPixels; dy += 1) {
      const row = cy * cellPixels + dy;
      if (row < h) {
        runs.push([row, start, end]);
      }
    }
  }
  runs.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: [number, number, number][] = [];
  for (const run of runs) {
    const last = merged[merged.length - 1];
    if (last !== undefined && last[0] === run[0] && last[2] + 1 >= run[1]) {
      last[2] = Math.max(last[2], run[2]);
    } else {
      merged.push([run[0], run[1], run[2]]);
    }
  }
  return { w, h, base: h, runs: merged };
}

/**
 * Applies overrides to a footprint table: `none` and empty cells leave a frame an empty footprint (it
 * blocks nothing and, having a footprint, is no longer filled by a tiles rule), cells replace it, an
 * override without cells keeps the blocking runs the frame had. A passage is laid beside them, with the
 * override's surface, and gives a frame a footprint even when it had none (a deck that blocks nothing); an
 * absent or empty passage leaves the frame without one, and without a surface. A frame whose size is unknown
 * keeps what it had.
 *
 * @param footprints Footprints by frame id.
 * @param overrides Overrides by frame id.
 * @param frameSize Looks a frame's pixel size up.
 * @param cellPixels Pixels per collision cell.
 * @returns A new table.
 */
export function applyFootprintOverrides(footprints: Readonly<Record<string, PropFootprint>>, overrides: Readonly<Record<string, FootprintOverride>>, frameSize: (frameId: string) => { readonly w: number; readonly h: number } | undefined, cellPixels: number): Record<string, PropFootprint> {
  const out: Record<string, PropFootprint> = { ...footprints };
  for (const [frameId, override] of Object.entries(overrides)) {
    if (override !== "none" && override.cells === undefined && override.passage === undefined) {
      continue;
    }
    const had = out[frameId];
    const size = frameSize(frameId) ?? had;
    if (size === undefined) {
      continue;
    }
    const empty: PropFootprint = { w: size.w, h: size.h, base: size.h, runs: [] };
    if (override === "none") {
      out[frameId] = empty;
      continue;
    }
    const own = override.cell ?? cellPixels;
    const opens = override.passage !== undefined && override.passage.length > 0 ? override.passage : null;
    const blocking: PropFootprint | undefined = override.cells !== undefined ? footprintFromCells(override.cells, size.w, size.h, own) : had === undefined ? undefined : { w: had.w, h: had.h, base: had.base, runs: had.runs };
    if (opens === null) {
      if (blocking !== undefined) {
        out[frameId] = blocking;
      }
      continue;
    }
    out[frameId] = { ...(blocking ?? empty), passage: footprintFromCells(opens, size.w, size.h, own).runs, ...(override.surface === undefined ? {} : { surface: override.surface }) };
  }
  return out;
}

/**
 * The footprint a frame holds once a change has merged into its library entry, so the editor shows what it
 * saved without reading the pack back. It follows the merge the library makes (`mergeOverride`): `none`
 * empties the frame, passage and all; cells replace what it blocks and keep its passage and surface; a passage
 * replaces the passage and the surface together, so leaving the surface out clears it and an empty passage
 * removes both.
 *
 * @param had The footprint the frame holds, or undefined when it has none.
 * @param change The change sent: `none`, or an object entry's fields.
 * @param size The frame's size in pixels.
 * @param cellPixels Pixels per collision cell.
 * @returns The footprint after the change, or undefined when the change holds neither cells nor a passage and
 *   so leaves the footprint as it was.
 */
export function footprintAfterChange(had: PropFootprint | undefined, change: FootprintOverride, size: { readonly w: number; readonly h: number }, cellPixels: number): PropFootprint | undefined {
  const empty: PropFootprint = { w: size.w, h: size.h, base: size.h, runs: [] };
  if (change === "none") {
    return empty;
  }
  if (change.cells === undefined && change.passage === undefined) {
    return undefined;
  }
  const own = change.cell ?? cellPixels;
  const blocking: PropFootprint = change.cells !== undefined ? footprintFromCells(change.cells, size.w, size.h, own) : had === undefined ? empty : { w: had.w, h: had.h, base: had.base, runs: had.runs };
  const passage = change.passage === undefined ? had?.passage : change.passage.length === 0 ? undefined : footprintFromCells(change.passage, size.w, size.h, own).runs;
  const surface = change.passage === undefined ? had?.surface : change.surface;
  return passage === undefined || passage.length === 0 ? blocking : { ...blocking, passage, ...(surface === undefined ? {} : { surface }) };
}

/**
 * A pack's overrides held to the surfaces the game lists (`SurfaceRules.names`): a deck whose `surface` is not
 * among them loses the name and keeps its passage, so it opens the ground as drawn and is heard as the ground
 * under it, and the frame is named for whoever reads the pack to say so. Dropping, where refusing would stop
 * every compile of every world that uses the pack over one misspelt name. A game that lists none holds no name to
 * anything, as a context written before the list holds none.
 *
 * @param overrides Overrides by frame id.
 * @param names The surfaces the game lists, or undefined when it lists none.
 * @returns The overrides, the ones given when every surface is listed, and each frame whose surface was dropped
 *   with the name it held, in the overrides' order.
 */
export function listedSurfaceOverrides(overrides: Readonly<Record<string, FootprintOverride>>, names: readonly string[] | undefined): { readonly overrides: Readonly<Record<string, FootprintOverride>>; readonly unlisted: readonly { readonly frameId: string; readonly surface: string }[] } {
  const unlisted: { frameId: string; surface: string }[] = [];
  if (names === undefined) {
    return { overrides, unlisted };
  }
  const held: Record<string, FootprintOverride> = {};
  for (const [frameId, override] of Object.entries(overrides)) {
    if (override === "none" || override.surface === undefined || names.includes(override.surface)) {
      held[frameId] = override;
      continue;
    }
    const { surface, ...rest } = override;
    unlisted.push({ frameId, surface });
    held[frameId] = rest;
  }
  return { overrides: unlisted.length === 0 ? overrides : held, unlisted };
}

/**
 * The frames whose drawing an override decides: true draws under characters, false sorts by row.
 *
 * @param overrides Overrides by frame id.
 * @returns Frame id to flag, for frames that carry one.
 */
export function groundFlags(overrides: Readonly<Record<string, FootprintOverride>>): Map<string, boolean> {
  const out = new Map<string, boolean>();
  for (const [frameId, override] of Object.entries(overrides)) {
    if (override !== "none" && override.ground !== undefined) {
      out.set(frameId, override.ground);
    }
  }
  return out;
}

/**
 * The frames whose override says whether they lie flat among props: true sorts by the top edge of the art, false
 * draws under every prop. Read where the frame is `ground` only; the flag itself is listed wherever it is stored.
 *
 * @param overrides Overrides by frame id.
 * @returns Frame id to flag, for frames that carry one.
 */
export function amongPropsFlags(overrides: Readonly<Record<string, FootprintOverride>>): Map<string, boolean> {
  const out = new Map<string, boolean>();
  for (const [frameId, override] of Object.entries(overrides)) {
    if (override !== "none" && override.amongProps !== undefined) {
      out.set(frameId, override.amongProps);
    }
  }
  return out;
}

/**
 * The frames whose fading an override decides: false never fades, true fades.
 *
 * @param overrides Overrides by frame id.
 * @returns Frame id to flag, for frames that carry one.
 */
export function fadeFlags(overrides: Readonly<Record<string, FootprintOverride>>): Map<string, boolean> {
  const out = new Map<string, boolean>();
  for (const [frameId, override] of Object.entries(overrides)) {
    if (override !== "none" && override.fades !== undefined) {
      out.set(frameId, override.fades);
    }
  }
  return out;
}

/**
 * The frames whose override carries a depth that says something (ADR-098 decision 8), a frame painted with no
 * floor included, for an asset build to cut into pieces and an editor to patch into its manifest. A stored
 * depth the validator refuses is passed over, so the frame draws whole; one in the old shape is read as its Walk on (`normaliseFrameDepth`); `depthIssues` names it. Nothing here
 * reaches a footprint, a chunk or a collision grid.
 *
 * Each depth is given in the cell its frame's ground is cut at (`groundCellOf`): a depth painted at the authoring
 * cell on an entry whose cells are finer is doubled exactly and names its `cell`. A frame whose entry has finer
 * cells and paints nothing gets a depth that says only its `cell`, so the ground of its footprint is cut as finely as
 * it was drawn (ADR-131 decision 13).
 *
 * @param overrides Overrides by frame id.
 * @param authoring The authoring cell in pixels, what an entry that names no cell measures.
 * @returns Frame id to depth, in the overrides' order.
 */
export function frameDepths(overrides: Readonly<Record<string, FootprintOverride>>, authoring: number = FOOTPRINT_DEFAULT_CELL): Map<string, FrameDepth> {
  const out = new Map<string, FrameDepth>();
  for (const [frameId, override] of Object.entries(overrides)) {
    if (override === "none") {
      continue;
    }
    const checked = override.depth === undefined ? undefined : validateFrameDepth(override.depth);
    const depth = checked !== undefined && checked.ok && !isEmptyFrameDepth(checked.depth) ? checked.depth : undefined;
    const cell = groundCellOf(override.cell, depth, authoring);
    if (depth === undefined && cell === authoring) {
      continue;
    }
    out.set(frameId, depth === undefined ? { cell } : depthAtCell(depth, depth.cell ?? authoring, cell, authoring));
  }
  return out;
}

/**
 * The cell, in pixels, each frame's ground is cut at when it is not the authoring cell: the frames whose entry or
 * whose painted depth is finer (`frameDepths`).
 *
 * @param overrides Overrides by frame id.
 * @param authoring The authoring cell in pixels.
 * @returns Frame id to cell, for the frames cut finer than the authoring cell.
 */
export function groundCells(overrides: Readonly<Record<string, FootprintOverride>>, authoring: number = FOOTPRINT_DEFAULT_CELL): Map<string, number> {
  const out = new Map<string, number>();
  for (const [frameId, depth] of frameDepths(overrides, authoring)) {
    if (depth.cell !== undefined && depth.cell !== authoring) {
      out.set(frameId, depth.cell);
    }
  }
  return out;
}

/**
 * The stored depths the validator refuses, by the rules that need no frame: each frame and the first reason,
 * for a compile to warn of and a builder to repaint. Such a depth stops nothing.
 *
 * @param overrides Overrides by frame id.
 * @returns The frames and reasons, in the overrides' order.
 */
export function depthIssues(overrides: Readonly<Record<string, FootprintOverride>>): { readonly frameId: string; readonly message: string }[] {
  const issues: { frameId: string; message: string }[] = [];
  for (const [frameId, override] of Object.entries(overrides)) {
    if (override === "none" || override.depth === undefined) {
      continue;
    }
    const checked = validateFrameDepth(override.depth);
    if (!checked.ok) {
      issues.push({ frameId, message: checked.message });
    }
  }
  return issues;
}

/**
 * The footprints overrides replace, so the editor can put a frame back to its derivation without a build
 * (P1.6-T11): for each frame whose override sets cells, a passage or `none`, what the table held before, or null when
 * the frame had no footprint. A frame already recorded keeps its record, so applying overrides over a table
 * that already carries some still names the derivation beneath them. Call it before
 * `applyFootprintOverrides` with the same table.
 *
 * @param footprints Footprints by frame id, before these overrides.
 * @param overrides Overrides by frame id.
 * @param recorded What earlier passes recorded.
 * @returns `recorded` with every frame these overrides replace added.
 */
export function derivedFootprintsUnder(footprints: Readonly<Record<string, PropFootprint>>, overrides: Readonly<Record<string, FootprintOverride>>, recorded: Readonly<Record<string, PropFootprint | null>>): Record<string, PropFootprint | null> {
  const out: Record<string, PropFootprint | null> = { ...recorded };
  for (const [frameId, override] of Object.entries(overrides)) {
    if ((override === "none" || override.cells !== undefined || override.passage !== undefined) && !(frameId in out)) {
      out[frameId] = footprints[frameId] ?? null;
    }
  }
  return out;
}

/**
 * The frames whose override paints a Behind or In front cell (ADR-132), for the compile to bake into its chunks and
 * the editor to lay on the open document (`standMarks`).
 *
 * @param overrides Overrides by frame id.
 * @param authoring The authoring cell in pixels, what an entry that names no cell measures.
 * @returns Frame id to its marks and the cell they are counted in, for frames that paint at least one cell.
 */
export function frameStandMarks(overrides: Readonly<Record<string, FootprintOverride>>, authoring: number = FOOTPRINT_DEFAULT_CELL): Map<string, FrameMarks> {
  const out = new Map<string, FrameMarks>();
  for (const [frameId, override] of Object.entries(overrides)) {
    if (override === "none") {
      continue;
    }
    const behind = override.behind ?? [];
    const front = override.front ?? [];
    if (behind.length > 0 || front.length > 0) {
      out.set(frameId, { cell: override.cell ?? authoring, behind, front });
    }
  }
  return out;
}

/**
 * The lights a pack's overrides give their frames (ADR-141's amendment of 2026-11-01, decision 1): each stored `light` read by
 * `readFrameLight`. A stored light this build refuses is left out and named, never a stop, as a stored depth is.
 *
 * @param overrides A pack's stored overrides, by frame id.
 * @returns The lights by frame id and one line for each light left out.
 */
export function frameLights(overrides: Readonly<Record<string, FootprintOverride>>): { readonly lights: Map<string, FrameLight>; readonly issues: readonly string[] } {
  const lights = new Map<string, FrameLight>();
  const issues: string[] = [];
  for (const [id, entry] of Object.entries(overrides)) {
    if (entry === "none" || entry.light === undefined || entry.light === null) continue;
    try {
      lights.set(id, readFrameLight(entry.light, `footprint override ${id}.light`));
    } catch (error) {
      issues.push(`${error instanceof Error ? error.message : String(error)}; the frame lights nothing`);
    }
  }
  return { lights, issues };
}
